import "@/assets/styles/main.scss";
import axios from "axios";
import "mapbox-gl/dist/mapbox-gl.css";
import * as Vue from "vue";
import VueAxios from "vue-axios";
import * as VueRouter from "vue-router";
import VueSvgInlinePlugin from "vue-svg-inline-plugin";
import "@vuepic/vue-datepicker/dist/main.css";
import "intro.js/introjs.css";

// This is the main template file for the application
import App from "@/App.vue";
import { mixins } from "@/helpers/mixins.js";

import {
  routes,
  checkRoute,
  checkSessionValidity
} from "@/helpers/routes.auth.helper.js";

import NotificationMessage from "@/components/shared/NotificationMessage.vue";
import AlertMessage from "@/components/shared/AlertMessage.vue";
import { safeHtmlDirective } from "@/helpers/sanitize.helper.js";
import i18n from "@/i18n";
import CommonHelper from "@/helpers/common.helper";

// This is an enum containing all the pages in the application
const PAGES = CommonHelper.PAGES;

// This loads the application and sets the languagetool
const app = Vue.createApp(App).use(i18n);

// This creates the router for the application and sets the routes from the import
const router = VueRouter.createRouter({
  history: VueRouter.createWebHistory(),
  routes
});

// This checks the route before each navigation if the user has
router.beforeEach(checkRoute);

window.dataLayer = window.dataLayer || [];

// This plugin reads the svg files and injects them into the html
app.use(VueSvgInlinePlugin, {
  attributes: {
    data: ["src"],
    remove: ["alt"]
  }
});
app.mixin(mixins);
app.directive("safe-html", safeHtmlDirective);
app.use(router);

// Axios is used to make http requests
app.use(VueAxios, axios);
app.component("NotificationMessage", NotificationMessage);
app.component("AlertMessage", AlertMessage);
axios.defaults.withCredentials = true;

// On startup we check if the session is still valid
checkSessionValidity(router);

// These paths should NOT trigger an automatic logout on 401, because they are
// auth-setup or asset paths where a 401 is expected and handled elsewhere.
const ignoredErrorPaths = [
  "brand.css",
  "checktwofactor",
  "setcookie",
  "logout"
];
const checkIfUrlContainsIngnoredPaths = (url) => {
  return ignoredErrorPaths.some((path) => url.includes(path));
};

// This is an interceptor that catches errors and logs them
let isInvalidatingSession = false;
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const statusCode = error.response?.status;
    console.log("===== ERROR CAUGHT ===== ", error);

    if (statusCode === 401) {
      let responseUrl = error.request.responseURL;
      // Trigger logout for all authenticated API calls, but ignore the auth-setup
      // and asset paths listed above (their 401s are expected and harmless).
      if (!checkIfUrlContainsIngnoredPaths(responseUrl)) {
        if (
          !isInvalidatingSession &&
          window.location.pathname !== "/" &&
          !window.location.pathname.includes("/mfa")
        ) {
          isInvalidatingSession = true;
          sessionStorage.setItem(
            "redirectAfterLogin",
            router.currentRoute.value.fullPath
          );
          await CommonHelper.invalidateSession(true);
          await router.push({ name: PAGES.Login });
          isInvalidatingSession = false;
        }
      }
    }
    if (statusCode === 500) {
      console.error(error.code, error.message);
    }
    return error.response;
  }
);

app.mount("#app");
